import config from '../config';

class VapiVariableService {
    constructor() {
        this.baseURL = config.apiBaseURL2 + 'vapi-variables';
    }

    /**
     * Get all variables
     * @returns {Promise}
     */
    async getAllVariables() {
        try {
            const response = await fetch(`${this.baseURL}/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            return await response.json();
        } catch (error) {
            console.error('Error fetching all variables:', error);
            throw error;
        }
    }

    /**
     * Get variables formatted for dropdown
     * @returns {Promise}
     */
    async getFormattedVariables() {
        try {
            const response = await fetch(`${this.baseURL}/formatted`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            return await response.json();
        } catch (error) {
            console.error('Error fetching formatted variables:', error);
            throw error;
        }
    }

    /**
     * Get variables by type
     * @param {string} type - The type of variables to fetch (default: 'context')
     * @returns {Promise}
     */ btoa
    async getVariablesByType(type = 'context',user_id = null) {
        try {
             let url=`${this.baseURL}/by-type`;
            //  use access token and refresh token from cookies in headers
            const accessToken = document.cookie.split('; ').find(row => row.startsWith('accessToken=')).split('=')[1];
            const refreshToken = document.cookie.split('; ').find(row => row.startsWith('refreshToken=')).split('=')[1];
            
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                    'Refresh-Token': `Bearer ${refreshToken}`,
                },
                body: JSON.stringify({type,user_id}),
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            return await response.json();
        } catch (error) {
            console.error('Error fetching variables by type:', error);
            throw error;
        }
    }

    /**
     * Get variable by key
     * @param {string} key - The key of the variable to fetch
     * @returns {Promise}
     */
    async getVariableByKey(key) {
        try {
            const response = await fetch(`${this.baseURL}/key/${key}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            return await response.json();
        } catch (error) {
            console.error('Error fetching variable by key:', error);
            throw error;
        }
    }

    /**
     * Create a new variable
     * @param {Object} variableData - The variable data to create
     * @returns {Promise}
     */
    async createVariable(variableData) {
        try {
            const response = await fetch(`${this.baseURL}/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify(variableData),
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            return await response.json();
        } catch (error) {
            console.error('Error creating variable:', error);
            throw error;
        }
    }

    /**
     * Update an existing variable
     * @param {number} id - The ID of the variable to update
     * @param {Object} variableData - The variable data to update
     * @returns {Promise}
     */
    async updateVariable(id, variableData) {
        try {
            const response = await fetch(`${this.baseURL}/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify(variableData),
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            return await response.json();
        } catch (error) {
            console.error('Error updating variable:', error);
            throw error;
        }
    }

    /**
     * Delete a variable
     * @param {number} id - The ID of the variable to delete
     * @returns {Promise}
     */
    async deleteVariable(id) {
        try {
            const response = await fetch(`${this.baseURL}/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            return await response.json();
        } catch (error) {
            console.error('Error deleting variable:', error);
            throw error;
        }
    }

    /**
     * Get fallback variables (hardcoded defaults in case API fails)
     * @returns {Array}
     */
    getFallbackVariables() {
        return [
            {
                key: "{{CURRENTDATE}}",
                value: "{{CURRENTDATE}}",
                text: "Current Date",
                label: "Current Date",
                description: "Current date (e.g., Wednesday, August 20, 2025)",
                type: "context",
                order: 1
            },
            {
                key: "{{CURRENTTIME}}",
                value: "{{CURRENTTIME}}",
                text: "Current Time",
                label: "Current Time",
                description: "Current time (e.g., 12:23 AM)",
                type: "context",
                order: 2
            },
            {
                key: "{{CURRENTDATETIME}}",
                value: "{{CURRENTDATETIME}}",
                text: "Current Date/Time",
                label: "Current Date/Time",
                description: "Current date and time (e.g., Wednesday, August 20, 2025 12:23 AM)",
                type: "context",
                order: 3
            }
        ];
    }
    async getUserTimezone(user_id) {
        try {
            const response = await fetch(`${this.baseURL}/getUserTimezone`, {
                method: 'POST',
                body: JSON.stringify({user_id:btoa(user_id)}),
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            return await response.json();
        } catch (error) {
            console.error('Error fetching user timezone:', error);
            throw error;
        }
    }
    async saveUserTimezone(user_id,timezone) {
        try {
            const response = await fetch(`${this.baseURL}/saveUserTimezone`, {
                method: 'POST',
                body: JSON.stringify({user_id:btoa(user_id),timezone}),
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error) {
            console.error('Error saving user timezone:', error);
            throw error;
        }
    }
}

export default new VapiVariableService();
